import { useState, useEffect, useCallback } from 'react';
import { gamificationService } from '@/services/gamificationService';
import type { Badge, GamificationLogEntry, LeaderboardEntry } from '@/types';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';

export function useGamification() {
  const { user } = useUnifiedAuth();
  const [totalPoints, setTotalPoints] = useState(0);
  const [history, setHistory] = useState<GamificationLogEntry[]>([]);
  const [badges, setBadges] = useState<(Badge & { earned: boolean })[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const [points, badgeData, lb] = await Promise.all([
        gamificationService.getPoints(),
        gamificationService.getBadges(),
        gamificationService.getLeaderboard(),
      ]);
      setTotalPoints(points.total_points);
      setHistory(points.history);
      setBadges(badgeData);
      setLeaderboard(lb);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading gamification');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetch(); }, [fetch]);

  return { totalPoints, history, badges, leaderboard, loading, error, refetch: fetch };
}
